import {BrowserRouter as Router, Route, Routes as Switch, Navigate} from "react-router-dom";
import PrivateRoute from "./components/PrivateRoute";
import {ThemeProvider} from "react-bootstrap";
import AppContext from "./AppContext";
import {enqueueSnackbar, SnackbarProvider} from "notistack";
import Dashboard from "./pages/Dashboard";
import Login from "./pages/Login";
import Logout from "./pages/Logout";
import Library from "./pages/Library";
import Account from "./pages/Account";
import './App.scss';
import {useContext, useEffect, useRef} from "react";
import API, {HandleError} from "./API";
import Loading from "./components/Loading";
import Simulation from "./pages/Simulation";
import Pricing from "./pages/Pricing";
import Checkout from "./pages/Checkout";
import OrderStatus from "./pages/OrderStatus";
import Practice from "./pages/Practice";
import Correct from "./assets/audio/Correct.mp3"
import Wrong from "./assets/audio/Wrong.mp3"
import Success from "./assets/audio/Success.mp3"
import Perfect from "./assets/audio/Perfect.mp3"
import Signup from "./pages/Signup";
import PasswordReset from "./pages/PasswordReset";
import Demo from "./pages/Demo";
import Help from "./pages/Help";
import OldVersion from "./pages/OldVersion";
import {useTranslation} from "react-i18next";
import ExtraVideos from "./pages/ExtraVideos";
import ExtraVideosPopup from "./components/ExtraVideosPopup";
function App() {
    const app = useContext(AppContext);
    const { t, i18n } = useTranslation()
    const appRef = useRef({});

    useEffect(() => {
        appRef.current = app;
    }, [app])

    useEffect(() => {
        console.log('locale changed', app.ui.locale)
        i18n.changeLanguage(app.ui.locale)
    }, [app.ui.locale])

    if(app?.user?.version > 0 && app?.user?.version !== 2) {
        window.location.href = process.env.REACT_APP_OLD_VERSION_URL;
    }

    const ping = async () => {
        if(!window.document.hidden && appRef?.current?.isLoggedIn()) {
            let lastPing = window.sessionStorage.getItem('last_ping');
            let minPing = new Date();

            minPing.setSeconds(minPing.getSeconds() - 30)
            if(lastPing === null || new Date(lastPing) < minPing) {
                window.sessionStorage.setItem('last_ping', (new Date()).toString());
                try {
                    const res = await API({
                        method: 'GET',
                        url: `/ping`,
                        headers: {
                            'Authorization': `Bearer ${appRef.current.token}`,
                        },
                        data: {}
                    })

                    if (res.status < 200 || res.status > 299)
                        throw res.data;

                    if(res.status === 205 && appRef.current.ui.new_version !== res.data.version) {
                        appRef.current.setUI({new_version: res.data.version})
                        enqueueSnackbar(<div dangerouslySetInnerHTML={{__html: '<strong>Nieuwe versie beschikbaar!</strong><br /><small>Gelieve de pagina te vernieuwen.</small>'}} />,
                            {variant: 'info', style: {whiteSpace: 'pre-line'}, autoHideDuration: 5000});
                    }
                    //appRef.current.storePing(res.data.user, res.data.ui);
                }
                catch (e) {
                    HandleError(e, 'PING', enqueueSnackbar, app.logout);
                }
            }
        }
    }
    useEffect(() => {
        setInterval(ping, 5000);
    }, [])

    //todo: find solution for this, because using setLoading freezes ui after 10ish clicks

    API.interceptors.request.use( (config) => {
        //setLoading(true)
        if(config?.url !== '/ping')
            document.getElementById('app-loading').className = 'visible';
        return config;
    }, (error) => {
        document.getElementById('app-loading').className = '';
        //setLoading(false)
        return Promise.reject(error);
    });
    API.interceptors.response.use((response) => {
        document.getElementById('app-loading').className = '';
        //setLoading(false)
        return response;
    },  (error) => {
        document.getElementById('app-loading').className = '';
        //setLoading(false)
        return Promise.reject(error);
    });

    return (
        <Router>
                <SnackbarProvider autoHideDuration={3000}
                                  anchorOrigin={{ vertical: 'top', horizontal: 'right' }}>
                    <ThemeProvider>
                        <Switch>
                            <Route path="/courses" exact={true} element={<PrivateRoute requireSubscription={true}><Library /></PrivateRoute>} />
                            <Route path="/practice" exact={true} element={<PrivateRoute demoRestricted={true} requireSubscription={true}><Practice /></PrivateRoute>} />
                            <Route path="/simulation" exact={true} element={<PrivateRoute demoRestricted={true} requireSubscription={true}><Simulation /></PrivateRoute>} />
                            <Route path="/extra-videos" exact={true} element={<PrivateRoute demoRestricted={true} requireSubscription={true}><ExtraVideos /></PrivateRoute>} />
                            <Route path="/account" exact={true} element={<PrivateRoute><Account /></PrivateRoute>} />
                            <Route path="/pricing/:course_id" element={<PrivateRoute demoRestricted={true}><Pricing /></PrivateRoute>} />
                            <Route path="/checkout/:plan_id" element={<PrivateRoute demoRestricted={true}><Checkout /></PrivateRoute>} />
                            <Route path="/order-status/:order_id" element={<PrivateRoute demoRestricted={true}><OrderStatus /></PrivateRoute>} />
                            <Route path="/demo" exact={true} element={<PrivateRoute><Demo /></PrivateRoute>} />
                            <Route path="/help" exact={true} element={<PrivateRoute><Help /></PrivateRoute>} />
                            <Route path="/login" exact={true} element={<Login />} />
                            <Route path="/signup" exact={true} element={<Signup />} />
                            <Route path="/password-reset" exact={true} element={<PasswordReset />} />
                            <Route path="/logout" exact={true} element={<Logout />} />
                            <Route path="/old-version" exact={true} element={<OldVersion />} />
                            <Route path="/" element={<PrivateRoute><Dashboard /></PrivateRoute>} />
                            <Route path="*" element={ <Navigate to="/" />} />
                        </Switch>
                    </ThemeProvider>
                </SnackbarProvider>
                <ExtraVideosPopup />
                <Loading show={false} /> {/* app.loading */}
                <audio controls={false} preload="auto" id="sfxCorrect">
                    <source src={Correct} type="audio/mpeg" />
                </audio>
                <audio controls={false} preload="auto" id="sfxWrong">
                    <source src={Wrong} type="audio/mpeg" />
                </audio>
                <audio controls={false} preload="auto" id="sfxSuccess">
                    <source src={Success} type="audio/mpeg" />
                </audio>
                <audio controls={false} preload="auto" id="sfxPerfect">
                    <source src={Perfect} type="audio/mpeg" />
                </audio>
        </Router>
    );
}

export default App;
